<template>
  <DetailTemplate
    :customClass="'claim-detail detail-page'"
    v-if="getPermission('claim:view')"
  >
    <template v-slot:sidebar-detail>
      <v-card class="h-100 mt-0" elevation="0">
        <div class="d-flex align-center">
          <div class="text-h5 fw-700">Claims Management</div>
          <v-spacer></v-spacer>
          <v-btn small @click="searchDialog = true" icon color="cyan" outlined>
            <v-icon>mdi-filter</v-icon></v-btn
          >
        </div>
        <div class="pt-2">
          <v-text-field
            v-model="search"
            placeholder="Search"
            prepend-inner-icon="mdi-magnify"
            hide-details
            clearable
            dense
            flat
            solo
          ></v-text-field>
        </div>

        <v-divider></v-divider>
        <v-row v-if="claimsLoading">
          <v-col cols="12">
            <v-skeleton-loader
              class="custom-skeleton"
              type="card"
              height="100"
            ></v-skeleton-loader>
          </v-col>
        </v-row>
        <template v-else>
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll custom-box-top-inner-shado"
            style="height: calc(100vh - 190px)"
          >
            <v-row v-if="claims && claims.length > 0">
              <v-col
                class="py-1"
                v-for="(item, i) in claims"
                :key="i"
                cols="12"
              >
                <v-card
                  class="claim-card px-4 py-2"
                  :class="{ active: selected === item.id }"
                  :style="
                    selected === item.id ? getBorderStyle(item.status) : {}
                  "
                  @click="toggleSelect(item.id)"
                  flat
                >
                  <div class="d-flex align-top w-100 justify-space-between">
                    <Barcode small :barcode="getItemBarcode(item)"></Barcode>
                    <div>
                      <div class="text-right">
                        <v-chip
                          class="fw-500"
                          small
                          :color="getStatusColor(item.status)"
                          text-color="white"
                          label
                        >
                          {{ getStatusText(item.status) }}
                        </v-chip>
                      </div>

                      <div>
                        <v-chip
                          v-if="item.status == 2"
                          class="custom-bold-button fw-500"
                          label
                          small
                          :color="
                            item.reimbursed_status === 'Reimbursed'
                              ? 'green lighten-4'
                              : 'orange lighten-4'
                          "
                          :text-color="
                            item.reimbursed_status === 'Reimbursed'
                              ? 'green darken-4'
                              : 'orange darken-4'
                          "
                        >
                          {{
                            item.reimbursed_status || "Reimbursement Pending"
                          }}
                        </v-chip>
                      </div>
                    </div>
                  </div>

                  <div class="font-weight-medium text-body-1 mt-1">
                    {{ item.title }}
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <div
                      class="amount blue--text text-darken-4 text-h6 fw-700 mt-1"
                    >
                      {{ item.currency_name }}
                      {{ (Number(item.amount) || 0).toFixed(2) }}
                    </div>
                    <v-chip label small outlined color="blue">
                      {{ getCategoryName(item.expense_category) }}
                    </v-chip>
                  </div>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col cols="12" class="py-4">
                <p
                  class="m-0 row-not-found text-center font-weight-500 font-size-16"
                >
                  <img
                    :src="$assetURL('media/error/empty.png')"
                    class="row-not-found-image"
                  />
                  <em class="text-muted"> No claims found</em>
                </p>
              </v-col>
            </v-row>
          </perfect-scrollbar>
        </template>
        <div v-if="false">
          <ContactDetail :claim="claimData" :page-loading="loading" />
        </div>
      </v-card>
    </template>
    <template v-slot:header-title>
      <v-skeleton-loader
        class="custom-skeleton"
        v-if="pageLoading"
        width="400"
        height="50"
        type="text"
      >
      </v-skeleton-loader>
      <template v-else>
        <div class="d-flex" v-if="claimData && claimData.claim_category">
          <v-tooltip
            v-if="claimData.title"
            top
            content-class="custom-top-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <h1
                v-bind="attrs"
                v-on="on"
                class="form-title custom-nowrap-ellipsis text-capitalize mr-2"
              >
                {{ claimData.title }}
              </h1>
            </template>
            <span>{{ claimData.title }}</span>
          </v-tooltip>
          <span v-if="claimData.status" class="ml-2">
            <CustomStatus
              v-if="false"
              :status="claimData.status"
              endpoint="claim/status"
            ></CustomStatus>

            <Barcode :barcode="claimData.barcode"></Barcode>
          </span>
        </div>
      </template>
    </template>
    <template v-slot:header-action>
      <v-skeleton-loader
        class="custom-skeleton d-inline-block mr-2"
        v-if="pageLoading"
        type="button"
        height="28"
      ></v-skeleton-loader>
      <template v-if="!pageLoading">
        <div class="d-flex align-top">
          <div class="d-flex" v-if="claimData.status">
            <div class="d-flex flex-column mr-2">
              <div>
                <v-chip
                  class="custom-bold-button fw-500"
                  style="height: 25px"
                  label
                  :color="getStatusColor(claimData.status)"
                  text-color="white"
                  >{{ getStatusText(claimData.status) }}</v-chip
                >
                <v-chip
                  v-if="claimData.status === 2"
                  class="custom-bold-button ml-2 fw-500"
                  style="height: 25px"
                  label
                  :color="
                    claimData.reimbursed_status === 'Reimbursed'
                      ? 'green lighten-4'
                      : 'orange lighten-4'
                  "
                  :text-color="
                    claimData.reimbursed_status === 'Reimbursed'
                      ? 'green darken-4'
                      : 'orange darken-4'
                  "
                >
                  {{ claimData.reimbursed_status || "Reimbursement Pending" }}
                </v-chip>
              </div>
            </div>
            <h1
              class="form-title-sgd text-h4 mr-2"
              v-if="claimData && claimData.amount"
            >
              <span>{{ claimData.currency_name }} {{ claimData.amount }} </span>
            </h1>
          </div>
          <v-btn
            v-if="
              claimData &&
              claimData.status == 2 &&
              claimData.reimbursed_status !== 'Reimbursed'
            "
            @click="openReimbursementDialog"
            depressed
            :disabled="pageLoading"
            class="mx-1 custom-bold-button white--text"
            color="green"
          >
            Reimbursed Payment
          </v-btn>
          <!-- More Button with Accept/Reject -->
          <v-menu
            v-if="claimData && claimData.status == 1 && checkApproveReject"
            transition="slide-y-transition"
            bottom
            content-class="custom-menu-list"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                depressed
                :disabled="statusLoading"
                class="mx-1 custom-bold-button white--text"
                color="red"
                v-bind="attrs"
                v-on="on"
                v-on:click="openRejectModal"
              >
                <v-icon left>mdi-close-thick</v-icon> Reject
              </v-btn>

              <v-btn
                depressed
                :disabled="statusLoading"
                class="mx-1 custom-bold-button white--text"
                color="green"
                v-bind="attrs"
                v-on="on"
                v-on:click="openAcceptModal"
              >
                <v-icon left>mdi-check-decagram-outline</v-icon> Approve
              </v-btn>
            </template>
          </v-menu>
          <template
            v-if="getPermission('claim:update') && claimData.status == 1"
          >
            <v-tooltip bottom content-class="custom-bottom-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :to="
                    getDefaultRoute('claim.create', {
                      query: { edit: claimData.id },
                    })
                  "
                  depressed
                  :disabled="pageLoading"
                  class="mx-1 custom-bold-button white--text"
                  color="cyan"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Click here</span>
            </v-tooltip>
          </template>
          <v-btn
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="goBack"
            depressed
          >
            <v-icon small left>mdi-keyboard-backspace</v-icon>
            Back
          </v-btn>
        </div>
      </template>
    </template>

    <template v-slot:body>
      <v-container class="pt-0 border-top px-0 overflow-hidden">
        <v-row>
          <v-col md="12" lg="7" class="">
            <perfect-scrollbar
              :options="{ suppressScrollX: true }"
              class="scroll custom-box-top-inner-shado"
              style="height: calc(100vh - 190px)"
            >
              <div class="mb-4 px-3">
                <ClaimOverview :claim="claimData" />
              </div>
              <div
                class="mb-4 px-3"
                v-if="selectedVisit && claimData.expense_category == 3"
              >
                <VisitProjectCard
                  :selected-visit="selectedVisit"
                  :loading="loading"
                />
              </div>
              <div
                class="mb-4 px-3"
                v-if="selectedProject && claimData.expense_category == 3"
              >
                <ClaimProjectCard
                  :selected-project="selectedProject"
                  :loading="loading"
                />
              </div>
              <div
                class="mb-4 px-3"
                v-if="selectedQuotation && claimData.expense_category == 3"
              >
                <QuotationCard
                  :selected-quotation="selectedQuotation"
                  :loading="loading"
                />
              </div>
              <div
                class="mb-4 px-3"
                v-if="selectedLead && claimData.expense_category == 4"
              >
                <LeadCard :selected-lead="selectedLead" :loading="loading" />
              </div>
              <div
                class="mb-4 px-3"
                v-if="selectedVisit && claimData.expense_category == 4"
              >
                <VisitProjectCard
                  :selected-visit="selectedVisit"
                  :loading="loading"
                />
              </div>
              <v-card
                v-if="claimData && claimData.reimbursed_status === 'Reimbursed'"
                class="mb-4 elevation-0 rounded px-3 py-2 left-border-green"
              >
                <div class="text-h6 green--text mb-2">
                  Reimbursed Information
                </div>
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <div
                      class="text-caption font-weight-bold text-uppercase grey--text text--darken-1"
                    >
                      Amount
                    </div>
                    <div class="text-h6 font-weight-medium green--text">
                      {{ claimData?.currency_name || "SGD" }}
                      {{
                        (Number(claimData?.reimbursed_amount) || 0).toFixed(2)
                      }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div
                      class="text-caption font-weight-bold text-uppercase grey--text text--darken-1"
                    >
                      Payment Mode
                    </div>
                    <div class="text-body-1 font-weight-medium">
                      {{
                        getReimbursedPaymentModeName(
                          claimData?.reimbursed_payment_mode
                        ) || "—"
                      }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div
                      class="text-caption font-weight-bold text-uppercase grey--text text--darken-1"
                    >
                      Date
                    </div>
                    <div class="text-body-1 font-weight-medium">
                      {{
                        formatReimbursedDate(claimData?.reimbursed_date) || "—"
                      }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12">
                    <div
                      class="text-caption font-weight-bold text-uppercase grey--text text--darken-1"
                    >
                      Remark
                    </div>
                    <div class="text-body-1 font-weight-medium">
                      <span v-if="claimData.reimbursed_remarks">{{
                        claimData.reimbursed_remarks
                      }}</span>
                      <em v-else class="text-muted">no remarks</em>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
              <template v-if="false">
                <v-divider></v-divider>
                <div class="text-h6 grey--text mb-2 mx-3">Activity Log</div>
                <div class="activity-log">
                  <div
                    v-for="(log, i) in activityLogs"
                    :key="i"
                    class="activity-item"
                  >
                    <!-- left vertical line -->
                    <div class="line" />

                    <!-- icon -->
                    <v-avatar size="28" class="mr-2">
                      <v-icon :color="log.color">{{ log.icon }}</v-icon>
                    </v-avatar>

                    <div class="flex-grow-1">
                      <div class="font-weight-medium text-body-1">
                        {{ log.title }}
                      </div>
                      <div class="text-body-2 text--secondary">
                        {{ log.subText }}
                      </div>
                      <div class="text-caption text--secondary">
                        {{ log.date }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </perfect-scrollbar>
          </v-col>
          <v-col md="12" lg="5" class="pt-0">
            <v-card style="position: sticky; top: 0px; z-index: 9">
              <v-card-text class="border-bottom">
                <v-card-title
                  class="pb-0 px-0 d-flex align-center justify-content-between"
                >
                  <div class="text-h6 text-uppercase font-weight-bold">
                    Receipt Attachment
                  </div>
                  <v-btn
                    v-if="false"
                    color="blue white--text"
                    width="24"
                    depressed
                    @click="downloadAllReceipts"
                  >
                    <v-icon>mdi-file-download</v-icon>
                  </v-btn>
                </v-card-title>
              </v-card-text>
              <!-- <pre> {{ claimAttachments }}</pre> -->
              <v-tabs
                v-model="claimTab"
                background-color="transparent"
                color="cyan"
                grow
                class="custom-tab-transparent sticky-tab-0"
              >
                <v-tab class="font-size-16 font-weight-600 px-8" href="#claim">
                  Claim
                  <v-avatar class="ml-2" color="cyan white--text" size="20"
                    >{{ claimAttachment.length }}
                  </v-avatar>
                </v-tab>
                <v-tab
                  v-if="getStatusText(claimData.status) === 'Approved'"
                  class="font-size-16 font-weight-600 px-8"
                  href="#approved"
                >
                  Approved
                  <v-avatar class="ml-2" color="green white--text" size="20">{{
                    approvedAttachments.length
                  }}</v-avatar>
                </v-tab>
                <v-tab
                  v-if="getStatusText(claimData.status) === 'Rejected'"
                  class="font-size-16 font-weight-600 px-8"
                  href="#rejected"
                >
                  Rejected
                  <v-avatar class="ml-2" color="red white--text" size="20">{{
                    rejectedAttachments.length
                  }}</v-avatar>
                </v-tab>
                <v-tab
                  v-if="claimData.reimbursed_status === 'Reimbursed'"
                  class="font-size-16 font-weight-600 px-8"
                  href="#reimbursed"
                >
                  Reimbursed
                  <v-avatar class="ml-2" color="cyan white--text" size="20">{{
                    reimbursementAttachments.length
                  }}</v-avatar>
                </v-tab>
              </v-tabs>
              <v-tabs-items v-model="claimTab">
                <v-tab-item value="claim">
                  <div class="px-5 py-3">
                    <!-- Loading Skeleton -->
                    <v-skeleton-loader
                      v-if="attachmentsLoading"
                      type="image"
                      height="300"
                    />

                    <!-- When attachments exist -->
                    <template v-else-if="claimAttachment.length > 0">
                      <v-carousel
                        :show-arrows="claimAttachment.length > 1"
                        cycle
                        :hide-delimiters="false"
                        height="400"
                      >
                        <v-carousel-item
                          v-for="(attachment, index) in claimAttachment"
                          :key="index"
                          class="image-wrapper"
                        >
                          <!-- LABEL -->
                          <div
                            class="top-label"
                            :style="{
                              background: getStatusLabel(attachment.type).color,
                            }"
                          >
                            {{ getStatusLabel(attachment.type).text }}
                          </div>

                          <template v-if="isImage(getExt(attachment))">
                            <v-img
                              :src="attachment.file.url"
                              contain
                              class="rounded-lg border h-100 cursor-pointer"
                              @click="openAttachment(attachment)"
                            >
                              <template #placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="grey lighten-5"
                                  />
                                </v-row>
                              </template>
                            </v-img>
                          </template>

                          <template v-else-if="isPDF(getExt(attachment))">
                            <v-card
                              class="rounded-lg h-100 cursor-pointer pa-4"
                              outlined
                              @click="openAttachment(attachment)"
                            >
                              <iframe
                                :src="
                                  attachment.file.url + '#toolbar=0&navpanes=0'
                                "
                                style="
                                  width: 100%;
                                  height: 100%;
                                  border: none;
                                  border-radius: 8px;
                                  pointer-events: none;
                                "
                                class="cursor-pointer"
                              ></iframe>
                            </v-card>
                          </template>

                          <template
                            v-else-if="
                              isExcel(getExt(attachment)) ||
                              isWord(getExt(attachment))
                            "
                          >
                            <v-card
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                              @click="openAttachment(attachment)"
                            >
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <div class="file-preview text-center">
                                  <v-icon size="64" color="blue darken-2"
                                    >mdi-file-document-outline</v-icon
                                  >
                                  <p>{{ attachment.file.name }}</p>

                                  <!-- <v-btn
                                  depressed
                                  color="blue"
                                  small
                                  @click="downloadAttachment(attachment)"
                                >
                                  <v-icon left small>mdi-download</v-icon>
                                  Download
                                </v-btn> -->
                                </div>
                              </v-row>
                            </v-card>
                          </template>
                          <template v-else>
                            <div class="file-preview cursor-pointer">
                              <v-icon size="64" color="grey lighten-2"
                                >mdi-file-document-outline</v-icon
                              >
                              <p>
                                {{
                                  attachment.extension?.toUpperCase() || "FILE"
                                }}
                              </p>
                            </div>
                          </template>
                        </v-carousel-item>
                      </v-carousel>
                    </template>

                    <!-- Empty State -->
                    <template v-else>
                      <div class="text-center py-8 grey--text">
                        <v-icon size="64" color="grey lighten-2">
                          mdi-file-image-outline
                        </v-icon>
                        <div class="mt-2">No attachments available</div>
                      </div>
                    </template>
                  </div>
                </v-tab-item>
                <v-tab-item
                  value="approved"
                  v-if="getStatusText(claimData.status) === 'Approved'"
                >
                  <div class="px-5 py-3">
                    <!-- Loading Skeleton -->
                    <v-skeleton-loader
                      v-if="attachmentsLoading"
                      type="image"
                      height="300"
                    />

                    <!-- When attachments exist -->
                    <template v-else-if="approvedAttachments.length > 0">
                      <v-carousel
                        :show-arrows="approvedAttachments.length > 1"
                        cycle
                        :hide-delimiters="false"
                        height="400"
                      >
                        <v-carousel-item
                          v-for="(attachment, index) in approvedAttachments"
                          :key="index"
                          class="image-wrapper"
                        >
                          <!-- LABEL -->
                          <div
                            class="top-label"
                            :style="{
                              background: getStatusLabel(attachment.type).color,
                            }"
                          >
                            {{ getStatusLabel(attachment.type).text }}
                          </div>

                          <template v-if="isImage(getExt(attachment))">
                            <v-img
                              :src="attachment.file.url"
                              contain
                              class="rounded-lg border h-100 cursor-pointer"
                              @click="openAttachment(attachment)"
                            >
                              <template #placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="grey lighten-5"
                                  />
                                </v-row>
                              </template>
                            </v-img>
                          </template>

                          <template v-else-if="isPDF(getExt(attachment))">
                            <v-card
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                              @click="openAttachment(attachment)"
                            >
                              <iframe
                                :src="
                                  attachment.file.url + '#toolbar=0&navpanes=0'
                                "
                                style="
                                  width: 100%;
                                  height: 100%;
                                  border: none;
                                  border-radius: 8px;
                                  pointer-events: none;
                                "
                                class="cursor-pointer"
                              ></iframe>
                            </v-card>
                          </template>

                          <template
                            v-else-if="
                              isExcel(getExt(attachment)) ||
                              isWord(getExt(attachment))
                            "
                          >
                            <v-card
                              @click="openAttachment(attachment)"
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                            >
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <div class="file-preview text-center">
                                  <v-icon size="64" color="blue darken-2"
                                    >mdi-file-document-outline</v-icon
                                  >
                                  <p>{{ attachment.file.name }}</p>

                                  <!-- <v-btn
                                  depressed
                                  color="blue"
                                  small
                                  @click="downloadAttachment(attachment)"
                                >
                                  <v-icon left small>mdi-download</v-icon>
                                  Download
                                </v-btn> -->
                                </div>
                              </v-row>
                            </v-card>
                          </template>
                          <template v-else>
                            <div class="file-preview cursor-pointer">
                              <v-icon size="64" color="grey lighten-2"
                                >mdi-file-document-outline</v-icon
                              >
                              <p>
                                {{
                                  attachment.extension?.toUpperCase() || "FILE"
                                }}
                              </p>
                            </div>
                          </template>
                        </v-carousel-item>
                      </v-carousel>
                    </template>

                    <!-- Empty State -->
                    <template v-else>
                      <div class="text-center py-8 grey--text">
                        <v-icon size="64" color="grey lighten-2">
                          mdi-file-image-outline
                        </v-icon>
                        <div class="mt-2">No attachments available</div>
                      </div>
                    </template>
                  </div>
                </v-tab-item>
                <v-tab-item
                  value="rejected"
                  v-if="getStatusText(claimData.status) === 'Rejected'"
                >
                  <div class="px-5 py-3">
                    <!-- Loading Skeleton -->
                    <v-skeleton-loader
                      v-if="attachmentsLoading"
                      type="image"
                      height="300"
                    />

                    <!-- When attachments exist -->
                    <template v-else-if="rejectedAttachments.length > 0">
                      <v-carousel
                        :show-arrows="rejectedAttachments.length > 1"
                        cycle
                        :hide-delimiters="false"
                        height="400"
                      >
                        <v-carousel-item
                          v-for="(attachment, index) in rejectedAttachments"
                          :key="index"
                          class="image-wrapper"
                        >
                          <!-- LABEL -->
                          <div
                            class="top-label"
                            :style="{
                              background: getStatusLabel(attachment.type).color,
                            }"
                          >
                            {{ getStatusLabel(attachment.type).text }}
                          </div>

                          <template v-if="isImage(getExt(attachment))">
                            <v-img
                              @click="openAttachment(attachment)"
                              :src="attachment.file.url"
                              contain
                              class="rounded-lg border h-100 cursor-pointer"
                            >
                              <template #placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="grey lighten-5"
                                  />
                                </v-row>
                              </template>
                            </v-img>
                          </template>

                          <template v-else-if="isPDF(getExt(attachment))">
                            <v-card
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                              @click="openAttachment(attachment)"
                            >
                              <iframe
                                :src="
                                  attachment.file.url + '#toolbar=0&navpanes=0'
                                "
                                style="
                                  width: 100%;
                                  height: 100%;
                                  border: none;
                                  border-radius: 8px;
                                  pointer-events: none;
                                "
                              ></iframe>
                            </v-card>
                          </template>

                          <template
                            v-else-if="
                              isExcel(getExt(attachment)) ||
                              isWord(getExt(attachment))
                            "
                          >
                            <v-card
                              @click="openAttachment(attachment)"
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                            >
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <div class="file-preview text-center">
                                  <v-icon size="64" color="blue darken-2"
                                    >mdi-file-document-outline</v-icon
                                  >
                                  <p>{{ attachment.file.name }}</p>

                                  <!-- <v-btn
                                  depressed
                                  color="blue"
                                  small
                                  @click="downloadAttachment(attachment)"
                                >
                                  <v-icon left small>mdi-download</v-icon>
                                  Download
                                </v-btn> -->
                                </div>
                              </v-row>
                            </v-card>
                          </template>
                          <template v-else>
                            <div class="file-preview cursor-pointer">
                              <v-icon size="64" color="grey lighten-2"
                                >mdi-file-document-outline</v-icon
                              >
                              <p>
                                {{
                                  attachment.extension?.toUpperCase() || "FILE"
                                }}
                              </p>
                            </div>
                          </template>
                        </v-carousel-item>
                      </v-carousel>
                    </template>

                    <!-- Empty State -->
                    <template v-else>
                      <div class="text-center py-8 grey--text">
                        <v-icon size="64" color="grey lighten-2">
                          mdi-file-image-outline
                        </v-icon>
                        <div class="mt-2">No attachments available</div>
                      </div>
                    </template>
                  </div>
                </v-tab-item>
                <v-tab-item
                  value="reimbursed"
                  v-if="claimData.reimbursed_status === 'Reimbursed'"
                >
                  <div class="px-5 py-3">
                    <!-- Loading -->
                    <v-skeleton-loader
                      v-if="attachmentsLoading"
                      type="image"
                      height="300"
                    />

                    <!-- Attachments Available -->
                    <template v-else-if="reimbursementAttachments.length > 0">
                      <v-carousel
                        :show-arrows="reimbursementAttachments.length > 1"
                        cycle
                        :hide-delimiters="false"
                        height="400"
                      >
                        <v-carousel-item
                          v-for="(
                            attachment, index
                          ) in reimbursementAttachments"
                          :key="index"
                          class="image-wrapper"
                        >
                          <!-- LABEL -->
                          <div
                            class="top-label"
                            :style="{
                              background: getStatusLabel(attachment.type).color,
                            }"
                          >
                            {{ getStatusLabel(attachment.type).text }}
                          </div>

                          <template v-if="isImage(getExt(attachment))">
                            <v-img
                              @click="openAttachment(attachment)"
                              :src="attachment.file.url"
                              contain
                              class="rounded-lg border h-100 cursor-pointer"
                            >
                              <template #placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="grey lighten-5"
                                  />
                                </v-row>
                              </template>
                            </v-img>
                          </template>

                          <template v-else-if="isPDF(getExt(attachment))">
                            <v-card
                              @click="openAttachment(attachment)"
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                            >
                              <iframe
                                :src="
                                  attachment.file.url + '#toolbar=0&navpanes=0'
                                "
                                style="
                                  width: 100%;
                                  height: 100%;
                                  border: none;
                                  border-radius: 8px;
                                  pointer-events: none;
                                "
                              ></iframe>
                            </v-card>
                          </template>

                          <template
                            v-else-if="
                              isExcel(getExt(attachment)) ||
                              isWord(getExt(attachment))
                            "
                          >
                            <v-card
                              @click="openAttachment(attachment)"
                              class="rounded-lg h-100 cursor-pointer"
                              outlined
                            >
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <div class="file-preview text-center">
                                  <v-icon size="64" color="blue darken-2"
                                    >mdi-file-document-outline</v-icon
                                  >
                                  <p>{{ attachment.file.name }}</p>
                                </div>
                              </v-row>
                            </v-card>
                          </template>
                          <template v-else>
                            <div class="file-preview cursor-pointer">
                              <v-icon size="64" color="grey lighten-2"
                                >mdi-file-document-outline</v-icon
                              >
                              <p>
                                {{
                                  attachment.extension?.toUpperCase() || "FILE"
                                }}
                              </p>
                            </div>
                          </template>
                        </v-carousel-item>
                      </v-carousel>
                      <v-carousel
                        v-if="false"
                        show-arrows
                        :hide-delimiters="false"
                        cycle
                        height="500"
                      >
                        <v-carousel-item
                          v-for="(
                            attachment, index
                          ) in reimbursementAttachments"
                          :key="index"
                          class="image-wrapper"
                        >
                          <!-- LABEL -->
                          <div
                            class="top-label"
                            :style="{
                              background: getStatusLabel(attachment.type).color,
                            }"
                          >
                            {{ getStatusLabel(attachment.type).text }}
                          </div>
                          <!-- Image Preview -->
                          <template v-if="isImage(attachment.extension)">
                            <v-img
                              :src="attachment.file.url"
                              contain
                              class="rounded-lg border"
                            >
                              <template #placeholder>
                                <v-row
                                  class="fill-height ma-0"
                                  align="center"
                                  justify="center"
                                >
                                  <v-progress-circular
                                    indeterminate
                                    color="grey lighten-5"
                                  />
                                </v-row>
                              </template>
                            </v-img>
                          </template>
                          <!-- PDF Preview -->
                          <template v-else-if="isPDF(attachment.extension)">
                            <iframe
                              :src="
                                attachment.file.url +
                                '#toolbar=0&navpanes=0&scrollbar=0'
                              "
                              style="
                                width: 100%;
                                height: 100%;
                                border: none;
                                border-radius: 8px;
                                pointer-events: none;
                              "
                            ></iframe>
                            <!-- Download button for PDF -->
                            <div class="pdf-download-container">
                              <v-btn
                                small
                                color="blue"
                                text
                                @click.stop="downloadAttachment(attachment)"
                              >
                                <v-icon small left>mdi-download</v-icon>
                                Download
                              </v-btn>
                            </div>
                          </template>
                          <!-- Unsupported File Type (including Excel) -->
                          <template v-else>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <div class="text-center">
                                <v-icon
                                  size="64"
                                  color="grey lighten-2"
                                  class="mb-2"
                                >
                                  mdi-file-document-outline
                                </v-icon>
                                <div class="grey--text">
                                  {{
                                    attachment.extension?.toUpperCase() ||
                                    "FILE"
                                  }}
                                </div>
                                <v-btn
                                  small
                                  color="blue"
                                  text
                                  @click="downloadAttachment(attachment)"
                                  class="mt-2"
                                >
                                  <v-icon small left>mdi-download</v-icon>
                                  Download
                                </v-btn>
                              </div>
                            </v-row>
                          </template>
                        </v-carousel-item>
                      </v-carousel>
                    </template>

                    <!-- Empty State -->
                    <template v-else>
                      <div class="text-center py-8 grey--text">
                        <v-icon size="64" color="grey lighten-2">
                          mdi-file-image-outline
                        </v-icon>
                        <div class="mt-2">
                          No reimbursement attachments available
                        </div>
                      </div>
                    </template>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </v-card>
            <div class="service-detail-height" v-if="false">
              <AdditionalDocuments
                v-if="claimData && claimData.id"
                :type-id="claimData.id"
                class="mx-0 mb-3"
                type-text="Claim Attachments"
                :type="34"
                :extensive="false"
              ></AdditionalDocuments>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template v-slot:footer>
      <Dialog
        v-if="detailDialog"
        :commonDialog="detailDialog"
        :dialogWidth="dialogWidth"
      >
        <template v-slot:title>Update Claim Details</template>
        <template v-slot:body>
          <v-form
            ref="claimDetailForm"
            v-model.trim="detailForm"
            lazy-validation
            v-on:submit.stop.prevent="onClaimDetailSubmit('claimDetailForm')"
          >
            <BasicDetails
              :detail="claimDetails"
              :isDialog="true"
            ></BasicDetails>
          </v-form>
        </template>
        <template v-slot:action>
          <template v-if="getPermission('claim:update')">
            <v-btn
              depressed
              :disabled="!detailForm || formLoading"
              :loading="formLoading"
              class="mx-2 custom-bold-button white--text"
              color="cyan"
              v-on:click="onClaimDetailSubmit('claimDetailForm')"
            >
              Update
            </v-btn>
          </template>
          <v-btn
            depressed
            :disabled="formLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="closeClaimDialog"
          >
            Close
          </v-btn>
        </template>
      </Dialog>

      <!-- Accept Modal -->
      <Dialog
        v-if="acceptDialog"
        :commonDialog="acceptDialog"
        :dialogWidth="600"
        persistent
      >
        <template v-slot:title>Confirmation</template>

        <template v-slot:body>
          <v-form
            ref="acceptForm"
            v-model.trim="acceptFormValid"
            lazy-validation
          >
            <v-container class="px-10" fluid>
              <p class="font-weight-600 font-size-19">
                Are you sure, you want to
                <span class="font-weight-700">Mark as Approved</span>?
              </p>

              <v-layout class="my-4">
                <v-flex>
                  <label class="font-weight-500 font-size-16 required"
                    >Remark</label
                  >
                  <TextAreaField
                    auto-grow
                    dense
                    filled
                    color="cyan"
                    v-model="acceptRemark"
                    placeholder="Remark"
                    solo
                    flat
                    row-height="25"
                    counter="200"
                    :rules="[(v) => !!v || 'Remark is required']"
                  />
                </v-flex>
              </v-layout>

              <v-layout class="mt-4">
                <v-flex md12>
                  <label class="font-weight-500 font-size-16 required"
                    >Amount</label
                  >

                  <v-text-field
                    v-model="acceptAmount"
                    :rules="[(v) => !!v || 'Amount is required']"
                    dense
                    filled
                    color="cyan"
                    placeholder="Amount"
                    solo
                    flat
                    @keypress="($event) => validateNumber($event, acceptAmount)"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-layout class="mt-4">
                <v-flex md12>
                  <label class="font-weight-500 font-size-16">Attachment</label>

                  <file-upload v-model="acceptAttachments" :maxLimit="5">
                  </file-upload>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </template>

        <template v-slot:action>
          <v-btn
            depressed
            :disabled="statusLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="closeAcceptModal"
          >
            No! Close
          </v-btn>

          <v-btn
            depressed
            :disabled="!acceptFormValid || statusLoading"
            :loading="statusLoading"
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            v-on:click="submitAccept"
          >
            Yes! Continue
          </v-btn>
        </template>
      </Dialog>

      <!-- Reject Modal -->
      <Dialog
        v-if="rejectDialog"
        :commonDialog="rejectDialog"
        :dialogWidth="600"
      >
        <template v-slot:title>Confirmation</template>
        <template v-slot:body>
          <v-form
            ref="rejectForm"
            v-model.trim="rejectFormValid"
            lazy-validation
          >
            <v-container class="px-10" fluid>
              <p class="font-weight-600 font-size-19">
                Are you sure, you want to
                <span class="font-weight-700">Mark as Rejected</span>?
              </p>
              <v-layout class="my-4">
                <v-flex>
                  <label class="font-weight-500 font-size-16 required"
                    >Remark</label
                  >
                  <TextAreaField
                    auto-grow
                    dense
                    filled
                    color="cyan"
                    v-model="rejectRemark"
                    placeholder="Remark"
                    solo
                    flat
                    row-height="25"
                    counter="200"
                    :rules="[(v) => !!v || 'Remark is required']"
                  />
                </v-flex>
              </v-layout>
              <v-layout class="mt-4">
                <v-flex md12>
                  <label class="font-weight-500 font-size-16">Attachment</label>
                  <file-upload v-model="rejectAttachments" :maxLimit="5">
                  </file-upload>
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </template>
        <template v-slot:action>
          <v-btn
            depressed
            :disabled="statusLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="closeRejectModal"
          >
            No! Close
          </v-btn>
          <v-btn
            depressed
            :disabled="!rejectFormValid || statusLoading"
            :loading="statusLoading"
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            v-on:click="submitReject"
          >
            Yes! Continue
          </v-btn>
        </template>
      </Dialog>
      <!-- filter dialog -->
      <Dialog :commonDialog="searchDialog" :dialogWidth="600">
        <template v-slot:title>Filter Claims</template>
        <template v-slot:body>
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll custom-box-top-inner-shadow"
          >
            <v-container fluid class="pt-0 custom-search-filter">
              <v-row>
                <v-col md="12">
                  <label
                    class="v-label font-weight-500 font-size-13 text-capitalize"
                    >Date Range</label
                  >
                  <DateRangePicker
                    v-model="dates"
                    placeholder="Select Claim Date Range"
                  />
                </v-col>

                <v-col md="12">
                  <label
                    class="v-label font-weight-500 font-size-13 text-capitalize"
                    >Status</label
                  >
                  <v-select
                    :menu-props="{ offsetY: true }"
                    :items="statusList"
                    v-model="status"
                    item-text="text"
                    item-value="name"
                    placeholder="Filter Status"
                    dense
                    filled
                    hide-details
                    solo
                    flat
                    clearable
                    item-color="cyan"
                  >
                  </v-select>
                </v-col>
                <v-col md="12">
                  <label
                    class="v-label font-weight-500 font-size-13 text-capitalize"
                    >Category Filter</label
                  >
                  <v-select
                    :menu-props="{ offsetY: true }"
                    :items="expenseCategories"
                    v-model="category"
                    item-text="name"
                    item-value="id"
                    placeholder="Filter Category"
                    dense
                    filled
                    hide-details
                    solo
                    flat
                    clearable
                    item-color="cyan"
                  >
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </perfect-scrollbar>
        </template>
        <template v-slot:action>
          <v-btn
            depressed
            class="mx-2 custom-bold-button"
            color="orange white--text"
            @click="clearFilters"
          >
            Clear Filters
          </v-btn>
          <v-btn
            depressed
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            @click="applyFilters"
          >
            Apply Filter
          </v-btn>
          <v-btn
            depressed
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="searchDialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>
      <!-- Reimbursed dialog -->
      <Dialog :commonDialog="reimbursedDialog" :dialogWidth="800">
        <template v-slot:title> Payment</template>

        <template v-slot:body>
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll"
          >
            <v-container fluid class="pt-0">
              <v-row dense>
                <!-- AMOUNT -->
                <v-col cols="12" sm="6">
                  <div class="v-label font-weight-500 font-size-13 mb-1">
                    Amount
                  </div>
                  <v-text-field
                    dense
                    filled
                    solo
                    flat
                    placeholder="Amount"
                    type="number"
                    min="0"
                    v-model.trim.number="payment.amount"
                    hide-spin-buttons
                    @keypress="
                      ($event) => validateNumber($event, payment.amount)
                    "
                  />
                </v-col>

                <!-- PAYMENT MODE -->
                <v-col cols="12" sm="6">
                  <div class="v-label font-weight-500 font-size-13 mb-1">
                    Payment Mode
                  </div>
                  <v-select
                    :menu-props="{ offsetY: true }"
                    dense
                    filled
                    solo
                    flat
                    :items="modeList"
                    v-model="payment.payment_mode"
                    placeholder="Select Mode"
                  />
                </v-col>

                <!-- REMARKS -->
                <v-col cols="12">
                  <div class="v-label font-weight-500 font-size-13 mb-1">
                    Remarks
                  </div>
                  <v-textarea
                    dense
                    filled
                    solo
                    flat
                    auto-grow
                    placeholder="Remarks"
                    row-height="20"
                    v-model="payment.remarks"
                    @keypress="managePaymentNoteLimit"
                    @paste="onPaymentNotePaste"
                  />
                  <div class="text-right text-caption mt-1">
                    {{ payment.remarks?.length || 0 }}/200
                  </div>
                </v-col>

                <!-- ATTACHMENTS TITLE -->
                <v-col cols="12">
                  <file-upload v-model="payment.attachments" :maxLimit="5">
                  </file-upload>
                </v-col>
              </v-row>
            </v-container>
          </perfect-scrollbar>
        </template>

        <template v-slot:action>
          <v-btn
            depressed
            color="cyan"
            class="white--text mr-2 custom-bold-button"
          >
            Process Payment
          </v-btn>

          <v-btn
            depressed
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="reimbursedDialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>

      <!-- Reimbursement dialog -->
      <Dialog :commonDialog="reimbursementDialog" :dialogWidth="800">
        <template v-slot:title> Process Reimbursement</template>

        <template v-slot:body>
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll"
          >
            <v-form
              ref="reimbursementForm"
              v-model.trim="reimbursementFormValid"
              lazy-validation
            >
              <v-container fluid class="pt-0">
                <v-row dense>
                  <!-- AMOUNT -->
                  <v-col cols="12" sm="6">
                    <div
                      class="v-label font-weight-500 font-size-13 mb-1 required"
                    >
                      Amount
                    </div>
                    <v-text-field
                      dense
                      filled
                      solo
                      flat
                      placeholder="Amount"
                      type="number"
                      min="0"
                      v-model.trim.number="reimbursementData.amount"
                      :rules="[(v) => !!v || 'Amount is required']"
                      hide-spin-buttons
                      @keypress="
                        ($event) =>
                          validateNumber($event, reimbursementData.amount)
                      "
                    />
                  </v-col>

                  <!-- PAYMENT MODE -->
                  <v-col cols="12" sm="6">
                    <div
                      class="v-label font-weight-500 font-size-13 mb-1 required"
                    >
                      Payment Mode
                    </div>
                    <v-select
                      :menu-props="{ offsetY: true }"
                      dense
                      filled
                      solo
                      flat
                      :items="paymentModes"
                      v-model="reimbursementData.payment_mode"
                      item-text="name"
                      item-value="id"
                      placeholder="Select Mode"
                      :rules="[(v) => !!v || 'Payment Mode is required']"
                    />
                  </v-col>

                  <!-- REMARKS -->
                  <v-col cols="12">
                    <div class="v-label font-weight-500 font-size-13 mb-1">
                      Remarks
                    </div>
                    <v-textarea
                      dense
                      filled
                      solo
                      flat
                      auto-grow
                      placeholder="Remarks"
                      row-height="20"
                      v-model="reimbursementData.remarks"
                      @keypress="manageReimbursementNoteLimit"
                      @paste="onReimbursementNotePaste"
                    />
                    <div class="text-right text-caption mt-1">
                      {{ reimbursementData.remarks?.length || 0 }}/200
                    </div>
                  </v-col>

                  <!-- ATTACHMENTS TITLE -->
                  <v-col cols="12">
                    <file-upload
                      v-model="reimbursementData.attachments"
                      :maxLimit="5"
                    >
                    </file-upload>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </perfect-scrollbar>
        </template>

        <template v-slot:action>
          <v-btn
            depressed
            :disabled="!reimbursementFormValid || statusLoading"
            :loading="statusLoading"
            color="cyan"
            class="white--text mr-2 custom-bold-button"
            v-on:click="processReimbursement"
          >
            Process Reimbursement
          </v-btn>

          <v-btn
            depressed
            :disabled="statusLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="reimbursementDialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>
    </template>
  </DetailTemplate>
</template>

<script>
import DetailTemplate from "@/view/pages/partials/Detail-Template";
import CommonMixin from "@/core/plugins/common-mixin";
import moment from "moment-timezone";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);
import AdditionalDocuments from "@/view/pages/claim/Additional-Documents.vue";
import ContactDetail from "@/view/pages/claim/detail/overview/Contact-Detail.vue";
import ClaimOverview from "@/view/pages/claim/detail/overview/Claim-Overview.vue";
import VisitProjectCard from "@/view/pages/claim/detail/VisitProjectCard.vue";
import ClaimProjectCard from "@/view/pages/claim/detail/ClaimProjectCard.vue";
import QuotationCard from "@/view/pages/claim/detail/QuotationCard.vue";
import LeadCard from "@/view/pages/claim/detail/LeadCard.vue";
import BasicDetails from "@/view/pages/claim/create-or-update/Basic-Details.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import CustomStatus from "@/view/pages/partials/CustomStatus.vue";
import FileUpload from "@/view/components/app-component/FileInput";
import Barcode from "@/view/pages/partials/Barcode.vue";
import DateRangePicker from "@/view/components/DateRangePicker.vue";
import {
  GET,
  PUT,
  PATCH,
  CLEAR_ERROR,
} from "@/core/services/store/request.module";
import { SuccessEventBus, ErrorEventBus } from "@/core/lib/message.lib";
import Dialog from "@/view/pages/partials/DialogNew.vue";
import { currentUser } from "@/core/services/jwt.service";

export default {
  name: "claim-detail",
  title: "Claim",
  mixins: [CommonMixin],

  components: {
    DetailTemplate,
    ContactDetail,
    ClaimOverview,
    AdditionalDocuments,
    Dialog,
    BasicDetails,
    CustomStatus,
    FileUpload,
    Barcode,
    DateRangePicker,
    VisitProjectCard,
    LeadCard,
    ClaimProjectCard,
    QuotationCard,
  },

  data() {
    return {
      selectedVisit: null,
      selectedLead: null,
      selectedProject: null,
      selectedQuotation: null,
      searchDialog: false,
      reimbursedDialog: false,
      reimbursementDialog: false,
      reimbursementFormValid: true,
      reimbursementData: {
        amount: null,
        payment_mode: null,
        remarks: null,
        attachments: [],
      },

      status: null,
      category: null,
      dates: null,
      amount: null,
      statusList: [],
      search: null,
      selected: null,
      loadingItem: null,
      claimsLoading: false,
      payment: {
        amount: null,
        payment_mode: null,
        remarks: null,
        attachments: null,
      },
      // reimbursementDialog: false,
      modeList: ["Credit Card", "Cash", "Cheque"],
      docType: 34,
      claims: [],

      claimAttachments: [],
      reimbursementAttachments: [],
      attachmentsLoading: false,
      searchTimeout: null,

      // activityLogs will be computed dynamically
      claimTab: "claim",
      pageLoading: true,
      loading: true, // ADD THIS
      claimId: 0,
      claimData: {},
      // Claim Update Dialog Properties
      detailDialog: false, // Start with false
      formLoading: false,
      acceptAmount: "",
      claimDetails: {},
      detailForm: true,
      dialogWidth: 900,

      // Options for dropdowns
      expenseCategories: [],
      paymentModes: [],
      currencies: ["SGD", "USD", "EUR", "MYR"],

      // Status update
      statusLoading: false,
      acceptDialog: false,
      acceptFormValid: false,
      acceptRemark: "",
      acceptAttachments: [],
      rejectDialog: false,
      rejectRemark: "",
      rejectFormValid: false,
      rejectAttachments: [],
      activityLogs: [],
      imageExtensions: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
      pdfExtensions: ["pdf"],
      excelExtensions: ["xlsx", "xls"],
      wordExtensions: ["doc", "docx"],

      // Role-based approval
      claimCategorySettings: {},
      currentUserRole: null,
      canApproveReject: false,
    };
  },

  async created() {
    this.claimId = this.$route.params.id;
    if (!this.claimId || this.claimId <= 0) {
      this.$router.go(-1);
    }
    await this.fetchClaimCategorySettings();
    this.getClaim();
    this.getClaims();
    this.fetchClaimOptions();
    this.fetchAttachments();
    this.rowData = [];
    this.currentPage = 1;

    this.statusList = await this.getStatusList();
    // for active card
    this.selected = Number(this.claimId);
  },

  watch: {
    "$route.params.id"(newId) {
      if (newId && newId !== this.claimId) {
        this.claimId = newId;
        this.getClaim();
        this.fetchAttachments();
        // fetchVisitDetails will be called in getClaim
      }
    },
    search() {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getClaims();
      }, 500);
    },
  },

  methods: {
    validateNumber($event, data) {
      this.limitDecimal($event, data);
      if (this.claimData && this.claimData.amount) {
        this.limitMaxNumber($event, Number(this.claimData.amount));
      }
    },
    getStatusList() {
      const _this = this;
      return new Promise((resolve, reject) => {
        try {
          _this.$store
            .dispatch(GET, { url: "claim/status" })
            .then((response) => {
              resolve(response.data || []);
            })
            .catch((error) => {
              reject(error);
            });
        } catch (error) {
          reject(error);
        }
      });
    },
    openAttachment(attachment) {
      if (attachment?.file?.url) {
        window.open(attachment.file.url, "_blank");
      }
    },
    getBorderStyle(status) {
      const colors = {
        1: "#ff9800", // Pending
        2: "#4CAF50", // Approved
        3: "#F44336", // Rejected
      };

      return {
        border: `2px solid ${colors[status] || "#D1D5DB"}`,
      };
    },
    getExt(att) {
      return att.extension?.toLowerCase() || "";
    },
    isImage(ext) {
      return this.imageExtensions.includes(ext);
    },
    isPDF(ext) {
      return this.pdfExtensions.includes(ext);
    },
    isExcel(ext) {
      return this.excelExtensions.includes(ext);
    },
    isWord(ext) {
      return this.wordExtensions.includes(ext);
    },

    getStatusLabel(type) {
      const map = {
        5009: { text: "Rejected", color: "#ff4444" },
        5010: { text: "Approved", color: "#2ecc71" },
        5011: { text: "Reimbursed", color: "#2ecc71" },
        34: { text: "Claim", color: "#2196F3" },
        // #ff9800 Pending
      };

      return map[type] || { text: "Unknown", color: "#999" };
    },
    onNotePaste(e) {
      let copied = e.clipboardData.getData("Text");
      let preVal = this.detail.note;
      let finalVal = String(preVal) + String(copied);
      if (Number(finalVal.length) > 199) {
        let trimValue = finalVal.substring(0, 200);
        this.detail.note = trimValue;
        e.preventDefault();
      }
    },

    limitDecimal(event) {
      const value = event.target.value;
      const key = event.key;

      if (
        ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"].includes(key)
      )
        return;

      if (!/[0-9.]/.test(key)) {
        event.preventDefault();
        return;
      }

      if (key === "." && value.includes(".")) {
        event.preventDefault();
        return;
      }

      const decimalIndex = value.indexOf(".");
      if (decimalIndex !== -1) {
        const decimals = value.substring(decimalIndex + 1);
        if (
          decimals.length >= 2 &&
          event.target.selectionStart > decimalIndex
        ) {
          event.preventDefault();
        }
      }
    },

    manageNoteLimit(e) {
      if (this.detail.note && this.detail.note.length > 199) {
        e.preventDefault();
      }
    },
    manageReimbursementNoteLimit(e) {
      if (
        this.reimbursementData.remarks &&
        this.reimbursementData.remarks.length > 199
      ) {
        e.preventDefault();
      }
    },
    onReimbursementNotePaste(e) {
      let copied = e.clipboardData.getData("Text");
      let preVal = this.reimbursementData.remarks;
      let finalVal = String(preVal) + String(copied);
      if (Number(finalVal.length) > 199) {
        let trimValue = finalVal.substring(0, 200);
        this.reimbursementData.remarks = trimValue;
        e.preventDefault();
      }
    },
    managePaymentNoteLimit(e) {
      if (this.payment.remarks && this.payment.remarks.length > 199) {
        e.preventDefault();
      }
    },
    onPaymentNotePaste(e) {
      let copied = e.clipboardData.getData("Text");
      let preVal = this.payment.remarks;
      let finalVal = String(preVal) + String(copied);
      if (Number(finalVal.length) > 199) {
        let trimValue = finalVal.substring(0, 200);
        this.payment.remarks = trimValue;
        e.preventDefault();
      }
    },
    toggleSelect(id) {
      if (this.selected === id) {
        // this.selected = null;
      } else {
        this.selected = id;
        // Update the route to the selected claim
        this.$router
          .push({
            name: "admin.claim.detail",
            params: { id: id },
          })
          .catch(() => {});
      }
    },

    getStatusColor(status) {
      if (status == 1) return "orange";
      if (status == 2) return "green";
      if (status == 3) return "red";
      return "grey";
    },

    getStatusText(status) {
      if (status == 1) return "Pending";
      if (status == 2) return "Approved";
      if (status == 3) return "Rejected";
      return "Unknown";
    },

    getCategoryName(categoryId) {
      if (!categoryId) return "—";
      const category = this.expenseCategories.find(
        (cat) => cat.id == categoryId
      );
      return category ? category.name : "—";
    },

    getItemBarcode(item) {
      if (!item) return "";

      // Show only the claim barcode
      return item.claim_barcode || "";
    },

    getReimbursedPaymentModeName(paymentModeId) {
      if (!paymentModeId) return "—";
      const paymentMode = this.paymentModes.find(
        (mode) => mode.id == paymentModeId
      );
      return paymentMode ? paymentMode.name : "—";
    },

    formatReimbursedDate(date) {
      if (!date) return "—";
      return moment(date).format("DD MMM YYYY");
    },

    formatDate(date) {
      if (!date) return "—";
      return moment(date).format("MM/DD/YYYY [at] hh:mm A");
    },

    async fetchAttachments() {
      try {
        this.attachmentsLoading = true;

        // Fetch all attachments for this claim (type 34 includes 34, 5009, 5010, 5011)
        const response = await this.$store.dispatch(GET, {
          url: `documents/34/${this.claimId}`,
        });

        if (response.data) {
          // Filter claim attachments (type 34 = original claim, 5010 = approved attachments)
          // Convert to number for comparison since DB returns type as string
          this.claimAttachments = response.data.filter(
            (doc) =>
              Number(doc.type) === 34 ||
              Number(doc.type) === 5010 ||
              Number(doc.type) === 5009
          );

          // Filter reimbursement attachments (type 5011)
          this.reimbursementAttachments = response.data.filter(
            (doc) => Number(doc.type) === 5011
          );
        }
      } catch (error) {
        console.error("Error fetching attachments:", error);
        this.claimAttachments = [];
        this.reimbursementAttachments = [];
      } finally {
        this.attachmentsLoading = false;
      }
    },

    async fetchActivityLogs() {
      try {
        const response = await this.$store.dispatch(GET, {
          url: `claims/${this.claimId}/activity`,
        });

        if (response.data && response.data.rows) {
          // Transform the data to match the expected format
          this.activityLogs = response.data.rows.map((log) => ({
            icon: this.getActivityIcon(log.description),
            color: this.getActivityColor(log.description),
            title: this.getActivityTitle(log.description),
            subText: log.description,
            date: this.formatDate(log.added_at),
            rawDate: log.added_at,
          }));
        }
      } catch (error) {
        console.error("Error fetching activity logs:", error);
        this.activityLogs = [];
      }
    },

    getActivityIcon(description) {
      if (description.toLowerCase().includes("submitted"))
        return "mdi-file-document-outline";
      if (description.toLowerCase().includes("approved"))
        return "mdi-check-box-outline";
      if (description.toLowerCase().includes("rejected"))
        return "mdi-close-box-outline";
      if (description.toLowerCase().includes("reimbursed"))
        return "mdi-cash-multiple";
      if (description.toLowerCase().includes("updated"))
        return "mdi-pencil-outline";
      return "mdi-information-outline";
    },

    getActivityColor(description) {
      if (description.toLowerCase().includes("submitted")) return "blue";
      if (description.toLowerCase().includes("approved")) return "green";
      if (description.toLowerCase().includes("rejected")) return "red";
      if (description.toLowerCase().includes("reimbursed")) return "green";
      if (description.toLowerCase().includes("updated")) return "orange";
      return "grey";
    },

    getActivityTitle(description) {
      if (description.toLowerCase().includes("submitted"))
        return "Claim Submitted";
      if (description.toLowerCase().includes("approved"))
        return "Claim Approved";
      if (description.toLowerCase().includes("rejected"))
        return "Claim Rejected";
      if (description.toLowerCase().includes("reimbursed"))
        return "Payment Reimbursed";
      if (description.toLowerCase().includes("updated")) return "Claim Updated";
      return "Activity";
    },

    getClaim() {
      this.pageLoading = true;
      this.loading = true;

      this.$store
        .dispatch(GET, {
          url: `claims/${this.claimId}`,
        })
        .then((response) => {
          this.claimData = response.data;
          this.pageLoading = false;
          this.loading = false;

          this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Claim", route: "claim" },
            { title: "Detail" },
            { title: this.claimData.barcode },
          ]);
          if (
            this.claimData.visit > 0 &&
            (this.claimData.expense_category == 3 ||
              this.claimData.expense_category == 4)
          ) {
            let barcode = "-";
            let type = "";
            let typeId = 0;
            if (this.claimData.related_entity) {
              if (typeof this.claimData.related_entity === "object") {
                if (this.claimData.related_entity.visit) {
                  barcode = this.claimData.related_entity.visit.barcode || "-";
                  type = "visit";
                  typeId = this.claimData.visit;
                } else if (this.claimData.related_entity.project) {
                  barcode =
                    this.claimData.related_entity.project.barcode || "-";
                  type = "project";
                  typeId = this.claimData.project;
                }
              } else if (typeof this.claimData.related_entity === "string") {
                barcode = this.claimData.related_entity;
              }
            }
            if (
              this.claimData &&
              (this.claimData.expense_category == 3 ||
                this.claimData.expense_category == 4)
            ) {
              this.selectedVisit = {
                id: typeId,
                type: type,
                barcode: barcode,
                title:
                  this.claimData.visit_title || this.claimData.title || "-",
                site_location: this.claimData.site_location || "-",
                quote_details: this.claimData.quote_details || null,
                project_details: this.claimData.project_details || null,
                contact_person:
                  typeof this.claimData.contact_person === "object" &&
                  this.claimData.contact_person !== null
                    ? this.claimData.contact_person
                    : {
                        display_name: this.claimData.contact_person || "-",
                        primary_email: null,
                        primary_phone: null,
                      },
              };
              console.log("selected visit---", this.selectedVisit);
            }
          }
          if (
            // this.claimData.claim_type === "project" &&
            this.claimData.project > 0
          ) {
            // let barcode = "-";
            // if (this.claimData.related_entity) {
            //   if (typeof this.claimData.related_entity === "object") {
            //     if (this.claimData.related_entity.visit) {
            //       barcode = this.claimData.related_entity.visit.barcode || "-";
            //     } else if (this.claimData.related_entity.project) {
            //       barcode =
            //         this.claimData.related_entity.project.barcode || "-";
            //     }
            //   } else if (typeof this.claimData.related_entity === "string") {
            //     barcode = this.claimData.related_entity;
            //   }
            // }

            this.selectedProject = {
              id: Number(this.claimData.project),
              barcode: this.claimData.project_details.project_barcode,
              title:
                this.claimData.project_details.project_name ||
                this.project_details.project_name ||
                "-",
              site_location: this.claimData.project_site_location || "-",
              contact_person:
                typeof this.claimData.project_contact_person === "object" &&
                this.claimData.project_contact_person !== null
                  ? this.claimData.project_contact_person
                  : {
                      display_name:
                        this.claimData.project_contact_person || "-",
                      primary_email: null,
                      primary_phone: null,
                    },
            };
            console.log(this.selectedProject);
          }
          if (
            // this.claimData.claim_type === "project" &&
            this.claimData.quotation > 0
          ) {
            // let barcode = "-";
            // if (this.claimData.related_entity) {
            //   if (typeof this.claimData.related_entity === "object") {
            //     if (this.claimData.related_entity.visit) {
            //       barcode = this.claimData.related_entity.visit.barcode || "-";
            //     } else if (this.claimData.related_entity.project) {
            //       barcode =
            //         this.claimData.related_entity.project.barcode || "-";
            //     }
            //   } else if (typeof this.claimData.related_entity === "string") {
            //     barcode = this.claimData.related_entity;
            //   }
            // }

            this.selectedQuotation = {
              id: Number(this.claimData.quotation),
              barcode: this.claimData.quotation_details.quotation_barcode,
              title:
                this.claimData.quotation_details.quotation_title ||
                this.quotation_details.quotation_title ||
                "-",
              site_location: this.claimData.quotation_site_location || "-",
              contact_person:
                typeof this.claimData.quotation_contact_person === "object" &&
                this.claimData.quotation_contact_person !== null
                  ? this.claimData.quotation_contact_person
                  : {
                      display_name:
                        this.claimData.quotation_contact_person || "-",
                      primary_email: null,
                      primary_phone: null,
                    },
            };
            console.log(this.selectedQuotation);
          } else if (
            this.claimData.expense_category == 4 &&
            this.claimData.lead
          ) {
            this.selectedLead = this.claimData.lead;
            if (this.selectedLead == 0) {
              this.selectedLead = null;
            }
            console.log(JSON.stringify(this.selectedLead, null, 2));
          }

          // Fetch activity logs
          this.fetchActivityLogs();

          // Check if user has approval rights for this claim
          this.checkApprovalRights();
        })
        .catch(() => {
          this.goBack();
        });
    },

    getClaims() {
      this.claimsLoading = true;

      let queryParams = [];
      // Status filter - convert number to string value
      if (this.status) {
        const statusMap = {
          All: "all",
          Pending: "draft",
          Approved: "accepted",
          Rejected: "rejected",
          Reimbursed: "reimbursed",
          "Reimbursement Pending": "reimbursedpending",
        };

        const mapped = statusMap[this.status];

        if (mapped) {
          queryParams.push(`status=${mapped}`);
        }
      }

      // Category filter
      if (this.category) {
        queryParams.push(`expense_category=${this.category}`);
      }

      // Date range filter - backend expects 'dates' array
      if (this.dates && this.dates.length === 2) {
        queryParams.push(`dates[0]=${this.dates[0]}`);
        queryParams.push(`dates[1]=${this.dates[1]}`);
      }

      // Search filter
      if (this.search) {
        queryParams.push(`search=${encodeURIComponent(this.search)}`);
      }

      const queryString =
        queryParams.length > 0 ? "?" + queryParams.join("&") : "";

      this.$store
        .dispatch(GET, {
          url: `claims${queryString}`,
        })
        .then((response) => {
          this.claims = response.data?.rows || [];
          this.claimsLoading = false;
        })
        .catch((error) => {
          console.error("Claims fetch error:", error);
          this.claims = [];
          this.claimsLoading = false;
        });
    },

    applyFilters() {
      this.getClaims();
      this.searchDialog = false;
    },

    clearFilters() {
      this.status = null;
      this.category = null;
      this.dates = null;
      this.searchDialog = false;
      this.getClaims();
    },

    openClaimDialog() {
      this.formLoading = false; // Reset form loading state
      this.claimDetails = { ...this.claimData };

      // Convert expense_date to YYYY-MM-DD format if it exists
      if (this.claimDetails.expense_date) {
        // If the date is in a different format, convert it to YYYY-MM-DD
        const date = new Date(this.claimDetails.expense_date);
        if (!isNaN(date.getTime())) {
          this.claimDetails.expense_date = date.toISOString().split("T")[0];
        }
      }

      // Initialize attachments if not present
      if (!this.claimDetails.attachments) {
        this.claimDetails.attachments = [];
      }

      this.detailDialog = true;

      // Force Vue to update
      this.$nextTick(() => {
        console.log("NextTick - detailDialog:", this.detailDialog);
      });
    },

    closeClaimDialog() {
      this.detailDialog = false;
      this.claimDetails = {};
      this.$nextTick(() => {
        if (this.$refs.claimDetailForm) {
          this.$refs.claimDetailForm.reset();
        }
      });
    },

    async onClaimDetailSubmit(formRef) {
      const _this = this;

      try {
        if (!_this.$refs[formRef].validate()) {
          return false;
        }

        _this.formLoading = true;
        _this.$store.dispatch(CLEAR_ERROR, {});

        const formData = {
          title: _this.claimDetails.title,
          reference: _this.claimDetails.reference,
          company_name: _this.claimDetails.company_name,
          currency: _this.claimDetails.currency,
          expense_category: _this.claimDetails.expense_category,
          payment_mode: _this.claimDetails.payment_mode,
          amount: _this.claimDetails.amount,
          expense_date: _this.claimDetails.expense_date,
          note: _this.claimDetails.note,
        };

        await _this.$store.dispatch(PUT, {
          url: `claims/${_this.claimId}`,
          data: formData,
        });

        _this.closeClaimDialog();
        _this.getClaim();
        SuccessEventBus.$emit("update:success", "Claim updated successfully");
      } catch (error) {
        _this.formLoading = false;
        _this.logError(error);
        ErrorEventBus.$emit("update:error", "Failed to update claim");
      }
    },

    async fetchClaimOptions() {
      try {
        this.pageLoading = true;
        const { data } = await this.$store.dispatch(GET, {
          url: "claims/options",
        });

        if (data && data.claim_categories) {
          this.expenseCategories = data.claim_categories;
        }

        if (data && data.payment_modes) {
          this.paymentModes = data.payment_modes;
        }
      } catch (error) {
        this.logError(error);
      } finally {
        this.pageLoading = false;
      }
    },

    openAcceptModal() {
      this.acceptRemark = "";
      this.acceptFormValid = false;
      this.acceptAttachments = [];
      this.acceptDialog = true;
      this.acceptAmount = this.claimData.amount || "";
    },

    closeAcceptModal() {
      this.acceptDialog = false;
      this.acceptRemark = "";
      this.acceptAttachments = [];
      if (this.$refs.acceptForm) {
        this.$refs.acceptForm.reset();
      }
    },

    async submitAccept() {
      if (!this.$refs.acceptForm.validate()) {
        return;
      }

      const success = await this.updateClaimStatus(
        "mark_as_accepted",
        this.acceptRemark,
        this.acceptAttachments,
        this.acceptAmount
      );
      if (success) {
        SuccessEventBus.$emit("update:success", "Claim approved successfully");
      }
      this.closeAcceptModal();
    },

    openRejectModal() {
      this.rejectRemark = "";
      this.rejectFormValid = false;
      this.rejectAttachments = [];
      this.rejectDialog = true;
    },

    closeRejectModal() {
      this.rejectDialog = false;
      this.rejectRemark = "";
      this.rejectAttachments = [];
      if (this.$refs.rejectForm) {
        this.$refs.rejectForm.reset();
      }
    },

    async submitReject() {
      if (!this.$refs.rejectForm.validate()) {
        return;
      }

      const success = await this.updateClaimStatus(
        "mark_as_rejected",
        this.rejectRemark,
        this.rejectAttachments
      );
      if (success) {
        SuccessEventBus.$emit("update:success", "Claim rejected successfully");
      }
      this.closeRejectModal();
    },

    openReimbursementDialog() {
      this.reimbursementData.amount = this.claimData.amount || null;
      this.reimbursementData.payment_mode = null;
      this.reimbursementData.remarks = null;
      this.reimbursementData.attachments = [];
      this.reimbursementDialog = true;
    },

    async processReimbursement() {
      if (!this.$refs.reimbursementForm.validate()) {
        return;
      }

      try {
        this.statusLoading = true;

        const payload = {
          reimbursed_amount: this.reimbursementData.amount,
          reimbursed_payment_mode:
            this.reimbursementData.payment_mode?.id ||
            this.reimbursementData.payment_mode,
          reimbursed_remarks: this.reimbursementData.remarks,
          reimbursed_status: "Reimbursed",
          attachments: this.reimbursementData.attachments || [],
        };

        await this.$store.dispatch(PATCH, {
          url: `claims/${this.claimId}/reimbursement`,
          data: payload,
        });

        this.reimbursementDialog = false;
        // Refresh claim data
        this.getClaim();
        this.getClaims();
        this.fetchAttachments();
        SuccessEventBus.$emit(
          "update:success",
          "Reimbursement processed successfully"
        );
        return true;
      } catch (error) {
        this.logError(error);
        // ErrorEventBus.$emit(
        //   "update:error",
        //   error.response?.data?.message || "Failed to process reimbursement"
        // );
        return false;
      } finally {
        this.statusLoading = false;
      }
    },

    async updateClaimStatus(
      status,
      remark = null,
      attachments = [],
      amount = null
    ) {
      try {
        this.statusLoading = true;

        const payload = {
          status: status,
        };

        if (remark) {
          payload.remark = remark;
        }

        if (attachments && attachments.length > 0) {
          payload.attachments = attachments;
        }

        if (amount !== null && amount !== undefined) {
          payload.amount = amount;
        }

        await this.$store.dispatch(PATCH, {
          url: `claims/${this.claimId}/status`,
          data: payload,
        });

        // Refresh claim data
        this.getClaim();
        this.fetchAttachments();
        return true;
      } catch (error) {
        this.logError(error);
        ErrorEventBus.$emit(
          "update:error",
          error.response?.data?.message || "Failed to update claim status"
        );
        return false;
      } finally {
        this.statusLoading = false;
      }
    },

    async fetchClaimCategorySettings() {
      try {
        const response = await this.$store.dispatch(GET, {
          url: "setting/claim-category",
        });

        if (response.data && Array.isArray(response.data)) {
          // Create a map of category ID to approval roles
          response.data.forEach((category) => {
            this.claimCategorySettings[category.id] = {
              approval_roles: category.approval_roles || [],
              claim_type: category.claim_type,
            };
          });
        }
      } catch (error) {
        console.error("Error fetching claim category settings:", error);
      }
    },

    checkApprovalRights() {
      // Get current user
      const user = currentUser();
      const userRole = user?.role;

      console.log({ userRole });

      if (!userRole || !this.claimData || !this.claimData.approve_roles) {
        this.canApproveReject = false;
        return;
      }
      let approveReject = false;
      if (
        this.claimData.approve_roles &&
        this.claimData.approve_roles.length > 0
      ) {
        let approvalRoles = this.claimData.approve_roles.map(Number);
        if (approvalRoles.includes(Number(userRole))) {
          approveReject = true;
        }
      }
      this.canApproveReject = approveReject;
    },

    async downloadAttachment(attachment) {
      try {
        // Fetch all files as blob to force download
        const headers = {};
        const token = localStorage.getItem("access_token");

        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await fetch(attachment.file.url, {
          method: "GET",
          headers,
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download =
            attachment.name || `attachment.${attachment.extension || "file"}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the blob URL
          window.URL.revokeObjectURL(url);
        } else {
          console.error(
            `Failed to download ${attachment.name}: ${response.status}`
          );
          ErrorEventBus.$emit(
            "update:error",
            `Failed to download ${attachment.name}`
          );
        }
      } catch (error) {
        console.error(`Error downloading ${attachment.name}:`, error);
        ErrorEventBus.$emit(
          "update:error",
          `Failed to download ${attachment.name}`
        );
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    async downloadAllReceipts() {
      try {
        // Combine all attachments from both claim and reimbursement tabs
        const allAttachments = [
          ...this.claimAttachments,
          ...this.reimbursementAttachments,
        ];

        if (allAttachments.length === 0) {
          ErrorEventBus.$emit("update:error", "No files found to download");
          return;
        }

        // Download each file individually with delay to prevent browser blocking
        for (let index = 0; index < allAttachments.length; index++) {
          const attachment = allAttachments[index];

          // Wait for the previous download to start
          if (index > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          try {
            // Use the existing downloadAttachment method for consistency
            this.downloadAttachment(attachment);
          } catch (error) {
            console.error(`Error downloading attachment ${index + 1}:`, error);
          }
        }

        SuccessEventBus.$emit(
          "update:success",
          `Started downloading ${allAttachments.length} file(s)`
        );
      } catch (error) {
        console.error("Error downloading attachments:", error);
        ErrorEventBus.$emit("update:error", "Failed to download attachments");
      }
    },
  },

  computed: {
    checkApproveReject() {
      const user = currentUser();
      const claim = this.claimData;
      if (!user || !claim) return false;
      const category = Number(claim.expense_category);
      if (category === 4) {
        const roles = claim?.approve_roles || [];
        return roles.map(Number).includes(Number(user.id));
      }
      if (category === 3) {
        //  return claim.approve_roles.map(Number).includes(Number(user.id));
        const roles = claim?.approve_roles || [];
        return roles.map(Number).includes(Number(user.id));
      }
      if (!claim?.approve_roles || !user?.role) return false;
      const approvalRoles = (claim.approve_roles || []).map(Number);
      return approvalRoles.includes(Number(user.role));
    },
    claimAttachment() {
      return this.claimAttachments.filter((a) => a.type === "34");
    },

    approvedAttachments() {
      return this.claimAttachments.filter((a) => a.type === "5010");
    },

    rejectedAttachments() {
      return this.claimAttachments.filter((a) => a.type === "5009");
    },

    // reimbursedAttachments() {
    //   return this.attachments.filter((a) => a.type === "5011");
    // },
    getPageTitle() {
      if (this.lodash.isEmpty(this.claimData) === false) {
        return `${this.claimData.title} • ${this.claimData.barcode}`;
      }
      return "";
    },
    getContactDialogWith() {
      return "600px";
    },
    /* activityLogs() {
      const logs = [];
      const claim = this.claimData;

      if (!claim) return logs;

      // Claim Submitted (when added_at exists)
      if (claim.added_at) {
        logs.push({
          icon: "mdi-file-document-outline",
          color: "blue",
          title: "Claim Submitted",
          subText: `Submitted by ${
            claim.added_by_name || "User"
          } for ${this.getCategoryName(claim.expense_category)} expense`,
          date: this.formatDate(claim.added_at),
          rawDate: claim.added_at,
        });
      }

      // Claim Approved (when status = 2 and accepted_date exists)
      if (claim.status === 2 && claim.accepted_date) {
        logs.push({
          icon: "mdi-check-box-outline",
          color: "green",
          title: "Claim Approved",
          subText: `Approved with amount ${claim.currency_name || "SGD"} ${
            claim.amount || 0
          }. Payment processing initiated.`,
          date: this.formatDate(claim.accepted_date),
          rawDate: claim.accepted_date,
        });
      }

      // Claim Rejected (when status = 3 and rejected_date exists)
      if (claim.status === 3 && claim.rejected_date) {
        logs.push({
          icon: "mdi-close-box-outline",
          color: "red",
          title: "Claim Rejected",
          subText: `Rejected. ${
            claim.general_remark || "No additional remarks."
          }`,
          date: this.formatDate(claim.rejected_date),
          rawDate: claim.rejected_date,
        });
      }

      // Reimbursed (when reimbursed_status = 'Reimbursed' and reimbursed_date exists)
      if (claim.reimbursed_status === "Reimbursed" && claim.reimbursed_date) {
        logs.push({
          icon: "mdi-cash-multiple",
          color: "green",
          title: "Payment Reimbursed",
          subText: `Reimbursed ${claim.currency_name || "SGD"} ${
            claim.reimbursed_amount || claim.amount || 0
          } via ${
            this.getReimbursedPaymentModeName(claim.reimbursed_payment_mode) ||
            "Payment Mode"
          }.`,
          date: this.formatDate(claim.reimbursed_date),
          rawDate: claim.reimbursed_date,
        });
      }

      // Updated (when updated_at exists and different from added_at)
      if (claim.updated_at && claim.updated_at !== claim.added_at) {
        logs.push({
          icon: "mdi-pencil-outline",
          color: "orange",
          title: "Claim Updated",
          subText: `Claim details were modified.`,
          date: this.formatDate(claim.updated_at),
          rawDate: claim.updated_at,
        });
      }

      // Sort logs by rawDate (most recent first) - use raw date values instead of parsing formatted strings
      return logs.sort(
        (a, b) => moment(b.rawDate).valueOf() - moment(a.rawDate).valueOf()
      );
    }, */
  },
};
</script>
<style scoped>
.required:after {
  content: " *";
  color: red;
}

.claim-card {
  border: 2px solid #e2e8f0;
  background: #f8f9fc;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.claim-card:hover {
  background: #f1f5f9;
  border-color: #e2e8f0;
}

.claim-card.active {
  border-width: 2px !important;
  /* border: 2px solid #3478ff;
  background: #ffffff; */
}

.activity-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 10px;
  background: #f8fafc;
  margin-bottom: 12px;
}

.activity-item .line {
  position: absolute;
  left: 0px;
  top: 0px;
  width: 3px;
  height: 100%;
  background: #3f51b5;
  border-radius: 3px;
}

.activity-item:first-child .line {
  background: #0bb07b;
}

.image-wrapper {
  position: relative;
}

.top-label {
  display: none;
  position: absolute;
  /* top: 18px;
  right: -30px; */
  top: 30px;
  right: -44px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 18px;
  font-size: 13px;
  text-align: center;
  font-weight: 600;
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
  width: 180px;
  z-index: 10;
}

.form-title-sgd {
  color: #24326d;
  letter-spacing: 0;
  line-height: 1em;
}
</style>
